<template>
  <div>
    <v-row>
      <v-col cols="12">
        <AssessmentFilters :loading="loading" @filter="filterBy" />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" sm="6" md="8" class="text-start mt-3">
        <span class="textLight--text font-weight-medium text-h5">
          {{ $t("quiz.assessmentReport") }}
        </span>
      </v-col>

      <v-col cols="12" sm="6" md="4">
        <v-row>
          <v-col cols="12" sm="8">
            <v-text-field
              v-model="search"
              hide-details
              prepend-inner-icon="mdi-magnify"
              color="textLight"
              :placeholder="$t('course.search')"
              solo
              flat
              clearable
              class="shrink rounded-lg"
            />
          </v-col>

          <v-col cols="12" sm="4">
            <v-menu>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :disabled="loading"
                  height="45"
                  block
                  depressed
                  style="text-transform: unset"
                  class="rounded-lg textLight--text font-weight-thin button"
                  v-bind="attrs"
                  v-on="on"
                  :loading="exportBtnLoading"
                >
                  <v-icon>mdi-export</v-icon>
                  {{ $t("reports.export") }}
                </v-btn>
              </template>

              <v-list>
                <v-list-item
                  v-for="(item, i) in exportItems"
                  :key="i"
                  class="hand-cursor"
                >
                  <v-list-item-title @click="exportTo(item.value)">
                    <div class="d-flex align-center justify-space-between">
                      {{ item.text }}
                      <v-icon size="25" :color="item.iconColor" class="ms-2">{{
                        item.icon
                      }}</v-icon>
                    </div>
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <AssessmentReportsList
          :assessmentReports="assessmentReports"
          :loading="loading"
        />
      </v-col>
    </v-row>

    <v-row class="mt-3">
      <v-col cols="12" v-if="assessmentReports.length">
        <Pagination :length="totalPages" @onPageSelect="onPagination" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { excelExport } from "../methods/excelExport";

import AssessmentReportsList from "@/app/reports-module/components/AssessmentReportsList";
import Pagination from "@/app/core-module/components/Pagination";
import AssessmentFilters from "@/app/reports-module/components/AssessmentFilters";

export default {
  name: "AssessmentReports",
  components: {
    AssessmentFilters,
    Pagination,
    AssessmentReportsList,
  },
  data() {
    return {
      loading: true,
      assessmentReports: [],
      totalCount: 0,

      exportBtnLoading: false,
      exportItems: [
        {
          value: "xl",
          text: this.$t("reports.exportTo"),
          icon: "mdi-microsoft-excel",
          iconColor: "success",
        },
      ],

      pageInput: this.defaultPageInput(),
      search: null,
      debounceSearch: null,
      filterInput: {
        employee: null,
        quizContent: null,
        organization: null,
        dateFrom: null,
        dateTo: null,
      },
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / this.pageInput.limit);
    },
  },
  apollo: {
    getQuizAttempts() {
      return {
        query: require("../../quiz-module/api/query/getQuizAttempts.gql"),
        variables: this.getVariables(),
        result({ data, loading }) {
          if (data.getQuizAttempts?.quizAttempts) {
            this.assessmentReports = data.getQuizAttempts.quizAttempts ?? [];
            this.totalCount = data.getQuizAttempts.totalCount ?? 0;
          }

          this.loading = loading;
        },
      };
    },
  },
  watch: {
    search: {
      immediate: false,
      handler() {
        clearTimeout(this.debounceSearch);
        this.debounceSearch = setTimeout(this.apiSearch, 500);
      },
    },
  },
  methods: {
    getVariables() {
      return { pageInput: this.pageInput, filterInput: this.filterInput };
    },
    reFetch() {
      this.$apollo.queries.getQuizAttempts.refetch(this.getVariables());
    },

    exportTo(val) {
      if (val === "xl") this.exportToXL();
    },
    async exportToXL() {
      this.exportBtnLoading = true;

      const exportArr = await this.$apollo
        .query({
          query: require("../../quiz-module/api/query/getQuizAttempts.gql"),
          variables: {
            pageInput: this.defaultPageInput(0),
            filterInput: this.filterInput,
          },
        })
        .then(result => {
          const data = result?.data?.getQuizAttempts?.quizAttempts;

          if (data?.length) {
            return data.map(quizObj => {
              let obj = {};

              obj[this.$t("quiz.assessment")] = quizObj?.quizContent?.title
                ? this.getLocalisedValue(quizObj.quizContent.title)
                : " - ";

              obj[this.$t("reports.employee")] = quizObj?.employee?.user
                ?.firstName
                ? `${quizObj.employee.user.firstName || ""} ${
                    quizObj.employee.user.lastName || ""
                  }`
                : " - ";

              obj[this.$t("quiz.date")] = quizObj?.attempt?.attemptedAt
                ? this.$moment(parseInt(quizObj.attempt.attemptedAt)).format(
                    "DD MMM YYYY hh:mm a"
                  )
                : " - ";

              obj[this.$t("reports.quiz.totalMark")] = quizObj?.attempt
                ?.totalMark
                ? quizObj.attempt.totalMark
                : " - ";

              obj[this.$t("reports.quiz.passPercentage")] = quizObj?.quizContent
                ?.quiz?.passMark
                ? quizObj.quizContent.quiz.passMark + "%"
                : " - ";

              obj[this.$t("reports.quiz.score")] =
                quizObj?.attempt?.acquiredMark != null
                  ? quizObj?.attempt?.acquiredMark
                  : " - ";

              obj[this.$t("reports.revenues.status")] = quizObj?.attempt?.status
                ? quizObj.attempt.status
                : " - ";

              return obj;
            });
          }
        });

      if (exportArr?.length) {
        const fileName = `assessment-reports-${this.$moment().unix()}`;
        excelExport(exportArr, fileName);
      }

      this.exportBtnLoading = false;
    },

    async apiSearch() {
      const searchText = this.search?.trim() ?? "";

      if (searchText.length > 0) {
        this.pageInput = this.defaultPageInput();
        this.filterInput.search = searchText;
        this.reFetch();
      } else if (searchText.length === 0 && this.filterInput?.search != null) {
        this.pageInput = this.defaultPageInput();
        this.filterInput.search = null;
        this.reFetch();
      }
    },
    filterBy(key, value) {
      if (key === "organization") {
        this.pageInput = this.defaultPageInput();
        this.filterInput.organization = value;
        this.reFetch();
      }

      if (key === "employee") {
        this.pageInput = this.defaultPageInput();
        this.filterInput.employee = value;
        this.reFetch();
      }

      if (key === "quizContent") {
        this.pageInput = this.defaultPageInput();
        this.filterInput.quizContent = value;
        this.reFetch();
      }

      if (key === "dates") {
        this.pageInput = this.defaultPageInput();
        this.filterInput.dateFrom = value.startDate;
        this.filterInput.dateTo = value.endDate;
        this.reFetch();
      }
    },
    onPagination(val) {
      if (val > 0) {
        this.pageInput.skip = this.pageInput.limit * (val - 1);
        this.reFetch();
      }
    },
  },
};
</script>
